import { Box, Collapse, ListSubheader, Menu, MenuItem } from '@mui/material';
import {
  addSplitRow,
  addSupplier,
  addSupplierType,
  assignRowToSupplier,
  getCategory,
  removeSplitRow,
  renameSplitRow,
  setSplitRowAmount,
} from '@timelog/pm-shared';
import type { SupplierLookupItem } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { parseAmountInput, useContractEditing, useFinancials } from './editor';

import { useMe, useSupplierLookup } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineTextCell } from '@/components/planning/cells';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import { formatAmountAbbrev, formatAmount } from '@/lib';

/**
 * The sub-contractors split (BR-19): suppliers (expandable, nested type rows)
 * plus the Unassigned pool whose rows can be assigned to a supplier once the
 * vendor is known — shares rebase so the absolute amount is kept (CALC-17).
 *
 * Suppliers come from the register (v8): an anchored picker over
 * `/api/lookup/suppliers` stores a `SupplierRef` snapshot; a deliberate blank
 * line stays possible for costs whose supplier is not known yet.
 */

const RemoveButton = ({ onClick, size = 13 }: { onClick: () => void; size?: number }) => (
  <Box
    component="button"
    type="button"
    data-automation-id="sub-row-remove"
    onClick={onClick}
    sx={{
      width: size + 2,
      height: size + 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      bgcolor: 'transparent',
      color: 'var(--fg-3)',
      flex: 'none',
      p: 0,
      cursor: 'pointer',
      '&:hover': { color: 'var(--color-coral-80)' },
    }}
  >
    <MaterialSymbol name="close" size={size} />
  </Box>
);

const DashedButton = ({
  onClick,
  automationId,
  children,
}: {
  onClick: (event?: MouseEvent<HTMLElement>) => void;
  automationId: string;
  children: ReactNode;
}) => (
  <Box
    component="button"
    type="button"
    data-automation-id={automationId}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.5,
      border: 'var(--border-width-thin) dashed var(--border-divider)',
      borderRadius: 'var(--border-radius-pill)',
      bgcolor: 'transparent',
      fontSize: 11,
      fontFamily: 'var(--font-family)',
      color: 'var(--fg-3)',
      cursor: 'pointer',
      '&:hover': { borderColor: 'var(--color-purple-60)', color: 'var(--color-purple-80)' },
    }}
  >
    {children}
  </Box>
);

export const SupplierSection = () => {
  const { state, dispatch } = useFinancials();
  const { isEditing, startEditing, stopEditing } = useContractEditing();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [assignMenu, setAssignMenu] = useState<{ anchor: HTMLElement; rowId: string } | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [pickerQuery, setPickerQuery] = useState('');
  const { t } = useTranslation('pmFinancials');

  const me = useMe();
  const lookupsOn = (me.data?.features.lookups ?? 'off') !== 'off';
  const register = useSupplierLookup(pickerQuery, lookupsOn && pickerAnchor !== null);

  const category = getCategory(state.document, 'sub');
  if (category.billedVia) return null;

  const split =
    category.split?.kind === 'suppliers' ? category.split : { suppliers: [], unassigned: [] };
  const categoryAmount = category.amount;
  const entryCount = split.suppliers.length + split.unassigned.length;

  /**
   * Keyboard advance across the section's top-level rows (unassigned first,
   * then suppliers), name → amount → next row (v8). New suppliers come from
   * the register picker, so the chain ends after the last amount rather than
   * auto-adding.
   */
  const rowChain = [
    ...split.unassigned.map((row) => row.id),
    ...split.suppliers.map((row) => row.id),
  ];
  const advanceRows = (kind: 'name' | 'amount', rowId: string, intent: string) => {
    if (intent !== 'enter-next' && intent !== 'tab-next' && intent !== 'tab-prev') {
      stopEditing();
      return;
    }
    const index = rowChain.indexOf(rowId);
    if (intent === 'tab-prev') {
      if (kind === 'amount') startEditing(`split-name:sub:${rowId}`);
      else if (index > 0) startEditing(`split-amount:sub:${rowChain[index - 1]}`);
      else stopEditing();
      return;
    }
    if (kind === 'name') {
      startEditing(`split-amount:sub:${rowId}`);
      return;
    }
    const nextId = rowChain[index + 1];
    if (nextId !== undefined) startEditing(`split-name:sub:${nextId}`);
    else stopEditing();
  };

  const toggleExpand = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const nameCell = (id: string, name: string, opts?: { italic?: boolean; bold?: boolean }) => (
    <InlineTextCell
      automationId="sub-row-name"
      value={name}
      editing={isEditing(`split-name:sub:${id}`)}
      onStartEdit={() => startEditing(`split-name:sub:${id}`)}
      onCancel={stopEditing}
      advanceOnEnter
      onCommit={(raw, intent) => {
        const trimmed = raw.trim();
        if (trimmed !== '') {
          dispatch({
            type: 'APPLY_OP',
            op: (document) => renameSplitRow(document, 'sub', id, trimmed),
          });
        }
        advanceRows('name', id, intent);
      }}
      display={
        <Box
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: opts?.bold ? 500 : 400,
            fontStyle: opts?.italic ? 'italic' : 'normal',
            color: opts?.bold ? 'var(--fg-1)' : 'var(--fg-2)',
          }}
        >
          {name}
        </Box>
      }
    />
  );

  const addBlankSupplier = () => {
    setPickerAnchor(null);
    dispatch({
      type: 'APPLY_OP',
      op: (document) =>
        addSupplier(document, {
          id: crypto.randomUUID(),
          name: t('suppliers.DefaultSupplierName', {
            letter: String.fromCharCode(65 + split.suppliers.length),
          }),
        }),
    });
  };

  const pickSupplier = (item: SupplierLookupItem, capturedAt: string) => {
    setPickerAnchor(null);
    setPickerQuery('');
    dispatch({
      type: 'APPLY_OP',
      op: (document) =>
        addSupplier(document, {
          id: crypto.randomUUID(),
          name: item.name,
          supplierRef: {
            tlpId: item.tlpId,
            snapshot: {
              name: item.name,
              ...(item.discipline === undefined ? {} : { discipline: item.discipline }),
            },
            capturedAt,
          },
        }),
    });
  };

  const amountCell = (id: string, amount: number) => (
    <InlineTextCell
      automationId="sub-row-amount"
      value={amount > 0 ? formatAmount(Math.round(amount)) : ''}
      editing={isEditing(`split-amount:sub:${id}`)}
      onStartEdit={() => startEditing(`split-amount:sub:${id}`)}
      onCancel={stopEditing}
      advanceOnEnter
      onCommit={(raw, intent) => {
        const parsed = parseAmountInput(raw);
        if (parsed !== null) {
          dispatch({
            type: 'APPLY_OP',
            op: (document) => setSplitRowAmount(document, 'sub', id, parsed),
          });
        }
        advanceRows('amount', id, intent);
      }}
      align="right"
      inputWidth={84}
      inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 500 }}
      display={
        <Box
          component="span"
          sx={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 11,
            fontWeight: 500,
            color: amount > 0 ? 'var(--fg-1)' : 'var(--fg-3)',
          }}
        >
          {amount > 0 ? formatAmountAbbrev(Math.round(amount)) : t('suppliers.SetAmount')}
        </Box>
      }
    />
  );

  return (
    <Box>
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Box
          component="button"
          type="button"
          data-automation-id="split-toggle-sub"
          onClick={() => setOpen((value) => !value)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            border: `1px solid ${open ? 'var(--color-purple-60)' : 'var(--border-divider)'}`,
            borderRadius: 'var(--border-radius-pill)',
            bgcolor: open ? 'var(--bg-sunken)' : 'transparent',
            fontSize: 11,
            fontFamily: 'var(--font-family)',
            color: open ? 'var(--fg-1)' : 'var(--fg-3)',
            cursor: 'pointer',
          }}
        >
          <MaterialSymbol name={open ? 'unfold_less' : 'unfold_more'} size={13} />
          {open
            ? t('actions.Collapse')
            : entryCount > 0
              ? t('suppliers.ToggleCount', { n: entryCount })
              : t('suppliers.Toggle')}
        </Box>
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            borderTop: 'var(--border-width-thin) solid var(--border-divider)',
            bgcolor: 'var(--bg-sunken)',
          }}
          data-automation-id="supplier-section"
        >
          {/* unassigned pool */}
          {split.unassigned.length > 0 ? (
            <Box sx={{ borderBottom: '2px solid var(--border-divider)' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  bgcolor: 'var(--color-fog-10)',
                }}
              >
                <MaterialSymbol name="help" size={13} color="var(--fg-3)" />
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--fg-3)',
                    letterSpacing: MICRO_LABEL_TRACKING,
                    textTransform: 'uppercase',
                  }}
                >
                  {t('suppliers.Unassigned')}
                </Box>
                <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {t('suppliers.UnassignedHint')}
                </Box>
              </Box>
              {split.unassigned.map((row) => (
                <Box
                  key={row.id}
                  data-automation-id="sub-unassigned-row"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    pl: 3,
                    py: 0.5,
                    borderTop: 'var(--border-width-thin) solid var(--border-divider)',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {nameCell(row.id, row.name, { italic: true })}
                  </Box>
                  {amountCell(row.id, (categoryAmount * row.sharePct) / 100)}
                  <Box
                    component="button"
                    type="button"
                    data-automation-id="sub-assign"
                    onClick={(event: MouseEvent<HTMLElement>) =>
                      setAssignMenu({ anchor: event.currentTarget, rowId: row.id })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      border: 'var(--border-width-thin) solid var(--border-divider)',
                      borderRadius: 'var(--border-radius-pill)',
                      bgcolor: 'transparent',
                      fontSize: 11,
                      fontFamily: 'var(--font-family)',
                      color: 'var(--fg-3)',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'var(--color-purple-60)',
                        color: 'var(--color-purple-80)',
                      },
                    }}
                  >
                    <MaterialSymbol name="move_down" size={12} />
                    {t('suppliers.Assign')}
                  </Box>
                  <RemoveButton
                    onClick={() =>
                      dispatch({
                        type: 'APPLY_OP',
                        op: (document) => removeSplitRow(document, 'sub', row.id),
                      })
                    }
                    size={12}
                  />
                </Box>
              ))}
            </Box>
          ) : null}

          {/* suppliers */}
          {split.suppliers.map((supplier) => {
            const supplierAmount = (categoryAmount * supplier.sharePct) / 100;
            const isExpanded = expanded.has(supplier.id);
            return (
              <Box
                key={supplier.id}
                sx={{ borderBottom: 'var(--border-width-thin) solid var(--border-divider)' }}
              >
                <Box
                  data-automation-id="supplier-row"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1 }}
                >
                  <Box
                    component="button"
                    type="button"
                    data-automation-id="supplier-expand"
                    onClick={() => toggleExpand(supplier.id)}
                    sx={{
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      bgcolor: 'transparent',
                      borderRadius: 'var(--border-radius-small)',
                      color: 'var(--fg-3)',
                      flex: 'none',
                      p: 0,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'var(--bg-hover)' },
                    }}
                  >
                    <MaterialSymbol name={isExpanded ? 'expand_more' : 'chevron_right'} size={14} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {nameCell(supplier.id, supplier.name, { bold: true })}
                  </Box>
                  {amountCell(supplier.id, supplierAmount)}
                  <RemoveButton
                    onClick={() =>
                      dispatch({
                        type: 'APPLY_OP',
                        op: (document) => removeSplitRow(document, 'sub', supplier.id),
                      })
                    }
                  />
                </Box>
                <Collapse in={isExpanded}>
                  <Box
                    sx={{
                      bgcolor: 'var(--bg-panel)',
                      px: 1,
                      pl: 4,
                      pb: 1,
                      borderTop: 'var(--border-width-thin) solid var(--border-divider)',
                    }}
                  >
                    {supplier.types.map((type) => (
                      <Box
                        key={type.id}
                        data-automation-id="supplier-type-row"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 3,
                            height: 3,
                            borderRadius: '50%',
                            bgcolor: 'var(--color-purple-40)',
                            flex: 'none',
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>{nameCell(type.id, type.name)}</Box>
                        {amountCell(type.id, (supplierAmount * type.sharePct) / 100)}
                        <RemoveButton
                          onClick={() =>
                            dispatch({
                              type: 'APPLY_OP',
                              op: (document) => removeSplitRow(document, 'sub', type.id),
                            })
                          }
                          size={12}
                        />
                      </Box>
                    ))}
                    <Box sx={{ mt: 1 }}>
                      <DashedButton
                        automationId="supplier-add-type"
                        onClick={() => {
                          setExpanded((current) => new Set(current).add(supplier.id));
                          dispatch({
                            type: 'APPLY_OP',
                            op: (document) =>
                              addSupplierType(document, supplier.id, {
                                id: crypto.randomUUID(),
                                name: t('splitRows.DefaultType'),
                              }),
                          });
                        }}
                      >
                        <MaterialSymbol name="add" size={12} />
                        {t('suppliers.AddType')}
                      </DashedButton>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 1, py: 1 }}>
            <DashedButton
              automationId="sub-add-supplier"
              onClick={(event?: MouseEvent<HTMLElement>) => {
                // Register picker when lookups are on; the blank line otherwise.
                if (lookupsOn && event) setPickerAnchor(event.currentTarget);
                else addBlankSupplier();
              }}
            >
              <MaterialSymbol name="add" size={13} />
              {t('suppliers.AddSupplier')}
            </DashedButton>
            <DashedButton
              automationId="sub-add-unassigned"
              onClick={() =>
                dispatch({
                  type: 'APPLY_OP',
                  op: (document) =>
                    addSplitRow(document, 'sub', {
                      id: crypto.randomUUID(),
                      name: t('suppliers.DefaultDeliverable'),
                    }),
                })
              }
            >
              <MaterialSymbol name="add" size={13} />
              {t('suppliers.AddDeliverable')}
            </DashedButton>
          </Box>
        </Box>
      </Collapse>

      <Menu
        anchorEl={pickerAnchor}
        open={pickerAnchor !== null}
        onClose={() => {
          setPickerAnchor(null);
          setPickerQuery('');
        }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
        data-automation-id="supplier-picker"
      >
        <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 1.4, pt: 1, pb: 0.5 }}>
          <Box
            component="input"
            autoFocus
            data-automation-id="supplier-picker-search"
            placeholder={t('suppliers.SearchPlaceholder')}
            value={pickerQuery}
            onChange={(event) => setPickerQuery((event.target as HTMLInputElement).value)}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{
              width: '100%',
              fontFamily: 'var(--font-family)',
              fontSize: 11,
              px: 1,
              py: 0.75,
              border: 'var(--border-width-thin) solid var(--border-divider)',
              borderRadius: 'var(--border-radius-small)',
              outline: 'none',
              '&:focus': { borderColor: 'var(--color-purple-60)' },
            }}
          />
        </ListSubheader>
        {(register.data?.items ?? []).map((item) => (
          <MenuItem
            key={item.tlpId}
            data-automation-id="supplier-picker-option"
            onClick={() =>
              pickSupplier(item, register.data?.capturedAt ?? new Date().toISOString())
            }
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box component="span" sx={{ fontSize: 14 }}>
                {item.name}
              </Box>
              {item.discipline ? (
                <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  {item.discipline}
                </Box>
              ) : null}
            </Box>
          </MenuItem>
        ))}
        {register.isPending && pickerAnchor !== null ? (
          <MenuItem disabled>{t('suppliers.Searching')}</MenuItem>
        ) : null}
        {register.data !== undefined && register.data.items.length === 0 ? (
          <MenuItem disabled>{t('suppliers.NoMatch')}</MenuItem>
        ) : null}
        <MenuItem
          divider={false}
          data-automation-id="supplier-picker-blank"
          onClick={addBlankSupplier}
          sx={{ borderTop: 'var(--border-width-thin) solid var(--border-divider)', mt: 0.5 }}
        >
          <MaterialSymbol name="add" size={13} />
          <Box component="span" sx={{ ml: 1, fontSize: 11 }}>
            {t('suppliers.AddBlank')}
          </Box>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={assignMenu?.anchor ?? null}
        open={assignMenu !== null}
        onClose={() => setAssignMenu(null)}
      >
        {split.suppliers.length === 0 ? (
          <MenuItem disabled>{t('suppliers.NoSuppliers')}</MenuItem>
        ) : (
          split.suppliers.map((supplier) => (
            <MenuItem
              key={supplier.id}
              data-automation-id="sub-assign-option"
              onClick={() => {
                const rowId = assignMenu?.rowId;
                setAssignMenu(null);
                if (rowId) {
                  dispatch({
                    type: 'APPLY_OP',
                    op: (document) => assignRowToSupplier(document, rowId, supplier.id),
                  });
                }
              }}
            >
              {supplier.name}
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};
